import {useCallback, useLayoutEffect, useRef, type RefObject} from 'react';

export const VALUE_EVENTS = ['input', 'change'] as const;

function fit(el: HTMLTextAreaElement) {
	el.style.height = 'auto';
	el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
}

/**
 * Grows a textarea to fit its content. Bounds stay in CSS — `min-h` and `max-h` both beat the inline
 * `height` this sets, so the caller owns the floor and the cap without this knowing about either.
 */
export function useTextareaHeight(ref: RefObject<HTMLTextAreaElement | null>): void {
	const measured = useRef<string | undefined>(undefined);

	const measure = useCallback(() => {
		const el = ref.current;
		if (!el || el.value === measured.current) return;

		measured.current = el.value;
		fit(el);
	}, [ref]);

	useLayoutEffect(measure);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;

		VALUE_EVENTS.forEach(event => el.addEventListener(event, measure));
		return () => VALUE_EVENTS.forEach(event => el.removeEventListener(event, measure));
	}, [ref, measure]);
}
